import { type AbstractPowerSyncDatabase, SyncStreamConnectionMethod } from '@powersync/web'
import { isNative } from '@/lib/platform'
import { SupabasePowerSyncConnector } from './connector'
import { logPsLifecycle } from './lifecycle-log'

/** Common post-construction wiring shared by web + native factories.
 *  Returns the same instance so callers can compose `return setupConnection(new PowerSyncDatabase(...))`. */
export function setupConnection<T extends AbstractPowerSyncDatabase>(db: T): T {
  console.log('[PS-DIAG] PowerSyncDatabase created, calling connect()')
  // On native (WKWebView) the default HTTP streaming sync intermittently stalls
  // ~19s: WKWebView / Capacitor's scheme handler buffers the /sync/stream
  // ReadableStream, so downloaded ops sit at 0 then burst in at the end (same
  // class of WebKit streaming bug we already work around for uploads). The
  // WebSocket transport bypasses fetch-streaming entirely. Web is consistently
  // fast on HTTP, so only switch native. Verified on-device: ~1/4 cold syncs
  // hit the 19s stall on HTTP.
  const connectOptions = isNative()
    ? { connectionMethod: SyncStreamConnectionMethod.WEB_SOCKET }
    : undefined
  db.connect(new SupabasePowerSyncConnector(), connectOptions)
    .then(() => { console.log('[PS-DIAG] connect() resolved'); logPsLifecycle('connect-resolved') })
    .catch((e) => {
      console.error('[PS-DIAG] connect() rejected', e)
      logPsLifecycle('connect-rejected', e instanceof Error ? e.message : String(e))
    })
  // Only push a lifecycle entry when something we care about *changes* —
  // `connected` flipping or a download/upload error appearing — so the buffer
  // tracks transitions, not every status tick.
  let prevConnected: boolean | undefined
  let prevDownloadError: string | undefined
  db.registerListener({
    statusChanged: (s) => {
      const downloadError = (s.dataFlowStatus as { downloadError?: { message?: string } } | undefined)?.downloadError?.message
      const uploadError = (s.dataFlowStatus as { uploadError?: { message?: string } } | undefined)?.uploadError?.message
      console.log('[PS-DIAG] status', {
        connected: s.connected,
        connecting: s.connecting,
        hasSynced: s.hasSynced,
        lastSyncedAt: s.lastSyncedAt?.toISOString(),
        downloading: s.dataFlowStatus?.downloading,
        uploading: s.dataFlowStatus?.uploading,
        downloadError,
        uploadError,
      })
      if (s.connected !== prevConnected) {
        prevConnected = s.connected
        logPsLifecycle('status', `connected=${s.connected} hasSynced=${s.hasSynced}`)
      }
      if (downloadError !== prevDownloadError) {
        prevDownloadError = downloadError
        if (downloadError) logPsLifecycle('status', `downloadError: ${downloadError}`)
      }
    },
  })
  return db
}
